import React, { useMemo, useState } from "react";
import {
  Avatar, Box, Chip, IconButton, Link, ListItemIcon, ListItemText, Menu, MenuItem, Stack, TableCell, TableRow, Typography,
} from "@mui/material";
import MoreVertOutlinedIcon from "@mui/icons-material/MoreVertOutlined";
import PersonOutlineOutlinedIcon from "@mui/icons-material/PersonOutlineOutlined";
import BookOutlinedIcon from "@mui/icons-material/BookOutlined";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import { Row } from "../types";

// Deterministic initials + colour so the same name always renders the same tile.
// Fast, offline, no image request — a plain <Avatar> tile is enough for a
// data-table row. If a real photo comes through later, drop the initials block
// and pass photo URL to <Avatar src>.
const AVATAR_PALETTE = [
  ["#dbeafe", "#1d4ed8"], ["#dcfce7", "#166534"], ["#fef3c7", "#92400e"],
  ["#f3e8ff", "#6b21a8"], ["#ffe4e6", "#9f1239"], ["#e0f2fe", "#075985"],
];
function stableAvatar(name: string) {
  const trimmed = (name || "?").trim();
  const initials = trimmed
    .split(/\s+/).slice(0, 2).map((s) => s[0]?.toUpperCase() || "").join("") || "?";
  let hash = 0;
  for (let i = 0; i < trimmed.length; i++) hash = (hash * 31 + trimmed.charCodeAt(i)) >>> 0;
  const [bg, color] = AVATAR_PALETTE[hash % AVATAR_PALETTE.length];
  return { initials, bg, color };
}

// One table row for a student. The subject list is grouped by enrolled-grade
// with each subject rendered as a compact chip. Long lists collapse to the
// first four + "+N more" so the row height stays bounded.
type Props = {
  row: Row;
  onView: (studentId: number) => void;
  onManageSubjects: (studentId: number) => void;
};

const modeLabel = (m: string | null) =>
  m === "in_person" ? "In Person" : m === "online" ? "Online" : m === "hybrid" ? "Hybrid" : (m || "—");

const gradeLabel = (g: string | null) => {
  if (!g) return "—";
  if (/^\d+$/.test(g)) return `Grade ${g}`;
  return g; // SK, JK, Pre-K, Adult stay as-is
};

const statusChip = (s: string) => {
  const map: Record<string, any> = { Active: "success", Dropped: "error", Inactive: "default", Pending: "warning" };
  return <Chip size="small" color={map[s] ?? "default"} label={s} />;
};

export default function StudentRow({ row, onView, onManageSubjects }: Props) {
  const [expanded, setExpanded] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);

  // Group subjects by grade for the chip cluster.
  const groups = useMemo(() => {
    const map = new Map<string, { grade: string; subjects: string[] }>();
    for (const s of row.enrolled_subjects) {
      const key = gradeLabel(s.grade_name);
      if (!map.has(key)) map.set(key, { grade: key, subjects: [] });
      map.get(key)!.subjects.push(s.subject_name);
    }
    return Array.from(map.values());
  }, [row.enrolled_subjects]);

  const totalSubjects = row.enrolled_subjects.length;
  const shown = expanded ? totalSubjects : Math.min(totalSubjects, 4);
  const remaining = Math.max(0, totalSubjects - shown);

  let rendered = 0;

  const avatar = stableAvatar(row.student_name || "?");

  return (
    <TableRow hover sx={{ verticalAlign: "top", "&:nth-of-type(even)": { background: "#fafafa" } }}>
      {/* Student */}
      <TableCell>
        <Stack direction="row" spacing={1.25} alignItems="center">
          <Avatar sx={{ bgcolor: avatar.bg, color: avatar.color, width: 34, height: 34, fontSize: 13 }}>
            {avatar.initials}
          </Avatar>
          <Box minWidth={0}>
            <Link
              component="button" underline="hover"
              onClick={() => onView(row.student_id)}
              sx={{ fontWeight: 600, color: "#1b3a6b", textAlign: "left" }}>
              {row.student_name || "—"}
            </Link>
            <Typography variant="caption" color="text.secondary" display="block">
              ID {row.student_id}{row.reference_id ? ` · ${row.reference_id}` : ""}
            </Typography>
          </Box>
        </Stack>
      </TableCell>

      {/* School Grade */}
      <TableCell>
        {row.school_grade ? (
          <Chip size="small" variant="outlined" label={gradeLabel(row.school_grade)}
            sx={{ borderColor: "#c7d2fe", color: "#1b3a6b", fontWeight: 600 }} />
        ) : row.school_grade_status === "multiple_enrolled_grades" ? (
          <Chip size="small" color="warning" variant="outlined" label="Multiple enrolled grades" />
        ) : row.school_grade_status === "no_enrolled_subjects" ? (
          <Chip size="small" color="warning" variant="outlined" label="No enrolled subjects" />
        ) : row.school_grade_status === "needs_sync" ? (
          <Chip size="small" color="info" variant="outlined" label="Needs sync" />
        ) : (
          <Chip size="small" variant="outlined" label="—"
            sx={{ borderColor: "#c7d2fe", color: "#1b3a6b", fontWeight: 600 }} />
        )}
      </TableCell>

      {/* Enrolled subjects */}
      <TableCell>
        {totalSubjects === 0 ? (
          <Chip size="small" color="warning" variant="outlined" label="No enrolled subjects" />
        ) : (
          <Stack spacing={0.75}>
            {groups.map((g) => {
              if (rendered >= shown) return null;
              const take = Math.max(0, shown - rendered);
              const slice = g.subjects.slice(0, take);
              rendered += slice.length;
              if (slice.length === 0) return null;
              return (
                <Box key={g.grade}>
                  <Typography variant="caption" color="text.secondary" fontWeight={600}>
                    {g.grade}
                  </Typography>
                  <Box mt={0.25}>
                    {slice.map((s, i) => (
                      <Chip key={`${g.grade}-${i}`} size="small" label={s}
                        sx={{ mr: 0.5, mb: 0.5, background: "#eff6ff", color: "#1b3a6b", fontWeight: 500 }} />
                    ))}
                  </Box>
                </Box>
              );
            })}
            {remaining > 0 && (
              <Link component="button" underline="hover" variant="caption"
                onClick={() => setExpanded(true)} sx={{ mt: 0.25 }}>
                + {remaining} more
              </Link>
            )}
            {expanded && totalSubjects > 4 && (
              <Link component="button" underline="hover" variant="caption"
                onClick={() => setExpanded(false)}>
                Show less
              </Link>
            )}
          </Stack>
        )}
      </TableCell>

      {/* Study Mode */}
      <TableCell>
        <Chip size="small" variant="outlined" label={modeLabel(row.study_mode)}
          sx={{ borderColor: "#e2e8f0" }} />
      </TableCell>

      {/* Location */}
      <TableCell>
        <Stack direction="row" spacing={0.5} alignItems="center">
          <LocationOnOutlinedIcon fontSize="inherit" sx={{ color: "#64748b" }} />
          <Typography variant="body2">{row.location || <em style={{ color: "#94a3b8" }}>Not assigned</em>}</Typography>
        </Stack>
      </TableCell>

      {/* Status */}
      <TableCell>{statusChip(row.status)}</TableCell>

      {/* Actions */}
      <TableCell align="right">
        <IconButton size="small" onClick={(e) => setMenuAnchor(e.currentTarget)}>
          <MoreVertOutlinedIcon fontSize="small" />
        </IconButton>
        <Menu anchorEl={menuAnchor} open={!!menuAnchor} onClose={() => setMenuAnchor(null)}>
          <MenuItem onClick={() => { setMenuAnchor(null); onView(row.student_id); }}>
            <ListItemIcon><PersonOutlineOutlinedIcon fontSize="small" /></ListItemIcon>
            <ListItemText primary="View Student" />
          </MenuItem>
          <MenuItem onClick={() => { setMenuAnchor(null); onManageSubjects(row.student_id); }}>
            <ListItemIcon><BookOutlinedIcon fontSize="small" /></ListItemIcon>
            <ListItemText primary="Manage Enrolled Subjects" />
          </MenuItem>
        </Menu>
      </TableCell>
    </TableRow>
  );
}
